import { pickNum, pickStr } from '../utils/api-response-pick.util';

export type PlanAttributeType = 'Boolean' | 'Numeric' | 'Text';

export interface ClientProductAttributeDto {
  attributeId: number;
  name: string;
  description?: string | null;
  attributeType: PlanAttributeType;
  unit?: string | null;
  value?: string | null;
}

const PLAN_ATTRIBUTE_TYPES = new Set<string>(['Boolean', 'Numeric', 'Text']);

function normalizeAttributeType(raw: unknown): PlanAttributeType {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (PLAN_ATTRIBUTE_TYPES.has(trimmed)) {
      return trimmed as PlanAttributeType;
    }
    const lower = trimmed.toLowerCase();
    if (lower === 'boolean') return 'Boolean';
    if (lower === 'numeric') return 'Numeric';
    if (lower === 'text') return 'Text';
  }

  const n = Number(raw);
  if (n === 0) return 'Boolean';
  if (n === 1) return 'Numeric';
  if (n === 2) return 'Text';

  return 'Text';
}

export function normalizeClientProductAttribute(raw: unknown): ClientProductAttributeDto | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }

  const o = raw as Record<string, unknown>;
  const attributeId = pickNum(o, 'attributeId', 'AttributeId');
  const name = pickStr(o, 'name', 'Name');
  if (attributeId == null || !name) {
    return null;
  }

  return {
    attributeId,
    name,
    description: pickStr(o, 'description', 'Description'),
    attributeType: normalizeAttributeType(o['attributeType'] ?? o['AttributeType']),
    unit: pickStr(o, 'unit', 'Unit'),
    value: pickStr(o, 'value', 'Value')
  };
}

export function normalizeClientProductAttributesResponse(raw: unknown): ClientProductAttributeDto[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => normalizeClientProductAttribute(item))
    .filter((item): item is ClientProductAttributeDto => item != null);
}
